import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import {
  appointmentActionOutcomeSchema,
  type AppointmentAction,
  type AppointmentActionOutcome,
} from './appointment-actions-contracts'

export type AppointmentActionsRepository = {
  execute: (
    tenantId: string,
    appointmentId: string,
    action: AppointmentAction
  ) => Promise<AppointmentActionOutcome>
}

type RepositoryOptions = {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}

function endpoint(
  tenantId: string,
  appointmentId: string,
  action: AppointmentAction
) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/appointments/${encodeURIComponent(appointmentId)}/${action}`
}

export function createAppointmentActionsRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: RepositoryOptions): AppointmentActionsRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    execute(tenantId, appointmentId, action) {
      return client.request(endpoint(tenantId, appointmentId, action), {
        method: 'POST',
        body: {},
        responseSchema: appointmentActionOutcomeSchema,
      })
    },
  }
}

/**
 * Only answers the service gives before anything changes are refusals: an
 * invalid request (400), no access (401/403), no such appointment (404), not
 * eligible (409), or the practice system's own definitive no (502). Anything
 * else (a dropped connection, a 5xx, an unreadable answer) is unverified.
 */
export function appointmentActionError(error: unknown) {
  if (error instanceof ApiError && error.code === 'server') {
    if (error.status === 400) return 'invalid'
    if (error.status === 401 || error.status === 403) return 'forbidden'
    if (error.status === 404) return 'not-found'
    if (error.status === 409) return 'ineligible'
    if (error.status === 502) return 'rejected'
  }
  return 'unverified'
}
